import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  ArrowDownToLine,
  Check,
  Download,
  GripVertical,
  ImageIcon,
  Loader2,
  Upload,
  X,
} from "lucide-react"
import { useState } from "react"

import { FileDropzone } from "@/components/FileDropzone"
import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { RelatedTools } from "@/components/related-tools"
import { SEO } from "@/components/seo"
import { Button } from "@/components/ui/button"
import { useDocumentUpload } from "@/hooks/useDocumentUpload"
import { type ImageFile, useImageToPdf } from "@/hooks/useImageToPdf"
import { useOperationGuard } from "@/hooks/useOperationGuard"
import { cn } from "@/lib/utils"

const howItWorks = [
  { icon: Upload, text: "Upload images" },
  { icon: GripVertical, text: "Drag to reorder" },
  { icon: ArrowDownToLine, text: "Download as PDF" },
]

const variantConfig = {
  image: {
    title: "Convert Images to PDF Online - Free | StitchPDF",
    description:
      "Convert JPG and PNG images to PDF for free. Drag and drop to reorder pages. No software installation required. Works directly in your browser.",
    ogTitle: "Convert Images to PDF Online - StitchPDF",
    canonical: "https://stitchpdf.com/image-to-pdf",
    heading: "Image to PDF",
    subheading: "Convert your JPG or PNG images to a single PDF document",
    schemaName: "Image to PDF Converter - StitchPDF",
  },
  jpg: {
    title: "Convert JPG to PDF Online - Free | StitchPDF",
    description:
      "Convert JPG images to PDF for free. Drag and drop to reorder pages. No software installation required. Works directly in your browser.",
    ogTitle: "Convert JPG to PDF Online - StitchPDF",
    canonical: "https://stitchpdf.com/jpg-to-pdf",
    heading: "JPG to PDF",
    subheading: "Convert your JPG images to a single PDF document",
    schemaName: "JPG to PDF Converter - StitchPDF",
  },
  png: {
    title: "Convert PNG to PDF Online - Free | StitchPDF",
    description:
      "Convert PNG images to PDF for free. Drag and drop to reorder pages. No software installation required. Works directly in your browser.",
    ogTitle: "Convert PNG to PDF Online - StitchPDF",
    canonical: "https://stitchpdf.com/png-to-pdf",
    heading: "PNG to PDF",
    subheading: "Convert your PNG images to a single PDF document",
    schemaName: "PNG to PDF Converter - StitchPDF",
  },
} as const

type Variant = keyof typeof variantConfig

interface Props {
  limits: {
    daily_remaining: number
    max_file_size_mb: number
    max_pages: number
  }
  variant?: Variant
  authenticated: boolean
}

function SortableImageThumbnail({
  image,
  onRemove,
}: {
  image: ImageFile
  onRemove: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: image.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "group bg-card relative cursor-grab rounded-lg border p-2 transition-shadow",
        isDragging && "ring-primary shadow-lg ring-2",
      )}
    >
      <img
        src={image.previewUrl}
        alt={image.name}
        className="h-32 w-auto rounded object-contain"
      />
      <p className="text-muted-foreground mt-1 truncate text-xs">
        {image.name}
      </p>
      <Button
        variant="secondary"
        size="icon"
        className="absolute -top-2 -right-2 h-6 w-6 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  )
}

export default function ImageToPdf({
  limits,
  variant = "image",
  authenticated,
}: Props) {
  const config = variantConfig[variant]
  const currentTool =
    variant === "jpg"
      ? "jpg-to-pdf"
      : variant === "png"
        ? "png-to-pdf"
        : "image-to-pdf"
  const [images, setImages] = useState<ImageFile[]>([])
  const [hasReordered, setHasReordered] = useState(false)
  const { convertToPdf, processing, progress } = useImageToPdf()
  const { uploadDocument } = useDocumentUpload()
  const {
    remainingOperations,
    completed,
    guardOperation,
    markCompleted,
    Modals,
  } = useOperationGuard({
    dailyRemaining: limits.daily_remaining,
    authenticated,
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleFilesAdded = (files: File[]) => {
    const newImages: ImageFile[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
    }))
    setImages((prev) => [...prev, ...newImages])
  }

  const handleRemoveImage = (id: string) => {
    setImages((prev) => {
      const image = prev.find((img) => img.id === id)
      if (image) {
        URL.revokeObjectURL(image.previewUrl)
      }
      return prev.filter((img) => img.id !== id)
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setImages((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id)
        const newIndex = items.findIndex((i) => i.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
      setHasReordered(true)
    }
  }

  const handleConvert = async () => {
    if (images.length === 0) return
    if (!guardOperation()) return

    const blob = await convertToPdf(images)

    const filename = "images.pdf"
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)

    markCompleted()
    void uploadDocument(blob, filename, "image_to_pdf")
  }

  return (
    <>
      <SEO
        title={config.title}
        description={config.description}
        canonical={config.canonical}
        ogTitle={config.ogTitle}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: config.schemaName,
            description: config.description,
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Web Browser",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: `How to Convert ${config.heading}`,
            description: config.subheading,
            step: [
              {
                "@type": "HowToStep",
                name: "Upload images",
                text: "Select or drag and drop your image files.",
              },
              {
                "@type": "HowToStep",
                name: "Reorder images",
                text: "Drag images to arrange them in your preferred order.",
              },
              {
                "@type": "HowToStep",
                name: "Download PDF",
                text: "Click Convert & Download to get your PDF file.",
              },
            ],
          })}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3">
              <ImageIcon className="text-primary h-8 w-8" aria-hidden="true" />
              <h1 className="text-2xl font-bold">{config.heading}</h1>
            </div>
            <p className="text-muted-foreground mt-2">{config.subheading}</p>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              {howItWorks.map((step, i) => {
                const hasImages = images.length > 0
                const isComplete =
                  completed ||
                  (hasImages && i === 0) ||
                  (hasReordered && i === 1) ||
                  (processing && i <= 1)
                const isActive =
                  !completed &&
                  ((processing && i === 2) ||
                    (!processing &&
                      ((hasImages && !hasReordered && i === 1) ||
                        (hasImages && i === 2))))
                return (
                  <div
                    key={step.text}
                    className={`flex items-center gap-2 ${
                      isComplete
                        ? "text-green-700 dark:text-green-400"
                        : isActive
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                        isComplete
                          ? "bg-green-200 text-green-700 dark:bg-green-900 dark:text-green-400"
                          : isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-primary/10 text-primary"
                      }`}
                    >
                      {isComplete ? (
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <step.icon className="h-4 w-4" aria-hidden="true" />
                    <span>{step.text}</span>
                  </div>
                )
              })}
            </div>

            {images.length === 0 && (
              <div className="mt-8">
                <FileDropzone
                  onFilesAdded={handleFilesAdded}
                  accept={{
                    "image/jpeg": [".jpg", ".jpeg"],
                    "image/png": [".png"],
                  }}
                  label="Drag & drop JPG or PNG images here"
                />
              </div>
            )}

            {images.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {images.length} {images.length === 1 ? "image" : "images"}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {remainingOperations} operations remaining today
                  </p>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={images}
                    strategy={rectSortingStrategy}
                  >
                    <div className="mt-4 flex flex-wrap gap-4">
                      {images.map((image) => (
                        <SortableImageThumbnail
                          key={image.id}
                          image={image}
                          onRemove={() => handleRemoveImage(image.id)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <div className="mt-6">
                  <FileDropzone
                    onFilesAdded={handleFilesAdded}
                    accept={{
                      "image/jpeg": [".jpg", ".jpeg"],
                      "image/png": [".png"],
                    }}
                    compact
                  />
                </div>

                <div className="mt-8 flex gap-4">
                  <Button
                    onClick={() => void handleConvert()}
                    disabled={images.length === 0 || processing}
                    className="gap-2"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Converting... {Math.round(progress)}%
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Convert & Download
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            <RelatedTools currentTool={currentTool} />

            <section className="mt-16 border-t pt-12">
              <h2 className="text-xl font-semibold">About This Tool</h2>
              <div className="text-muted-foreground mt-4 space-y-4 text-sm leading-relaxed">
                <p>
                  Drop images into the box, drag to reorder, and download as a
                  single PDF. Supports JPG and PNG. Processing runs in your
                  browser.
                </p>
                <h3 className="text-foreground pt-4 font-medium">Features</h3>
                <ul className="list-inside list-disc space-y-2">
                  <li>
                    <strong>JPG and PNG:</strong> Common image formats supported
                  </li>
                  <li>
                    <strong>Batch conversion:</strong> Combine multiple images
                  </li>
                  <li>
                    <strong>Drag to reorder:</strong> Arrange pages before
                    converting
                  </li>
                  <li>
                    <strong>Original quality:</strong> No compression
                  </li>
                </ul>
                <h3 className="text-foreground pt-4 font-medium">Use Cases</h3>
                <p>
                  Create photo albums. Combine scanned documents. Convert
                  screenshots to PDFs. Archive images in a single file.
                </p>
              </div>
            </section>
          </div>
        </main>
        <PublicFooter />
      </div>

      <Modals />
    </>
  )
}
